import {
  ceilToStartOfDay,
  endOfContainingDay,
  type DateRange,
} from "@repo/app-types/entities/dateRange";
import { DateTime } from "luxon";

/** Human-readable label for a {@link DateRange} value — either a preset like
 * "Last 30 days" or a specific start/end date range. Stands on its own; for a
 * range named inside a sentence use {@link formatDateRangeCondition}. */
export function formatDateRange(value: DateRange): string {
  if (value.end !== undefined) {
    return formatAnchoredRange(value.end, value);
  }

  const length = measureRelativeLength(value);
  return length.unit === "days"
    ? `Last ${length.count} days`
    : `Last ${length.count} hours`;
}

/**
 * A whole filter sentence naming a {@link DateRange} — "is within last 30
 * days". Each phrasing is written out in full rather than embedding
 * {@link formatDateRange}, whose presets are capitalized for standalone use:
 * both the capitalization and the wording around the range are the
 * translator's to decide, and neither survives being pasted together.
 */
export function formatDateRangeCondition(value: DateRange): string {
  if (value.end !== undefined) {
    return `is within ${formatAnchoredRange(value.end, value)}`;
  }

  const length = measureRelativeLength(value);
  return length.unit === "days"
    ? `is within last ${length.count} days`
    : `is within last ${length.count} hours`;
}

/**
 * Like {@link formatDateRange}, but anchored ranges render verbose endpoints
 * with weekday names ("Mon Jun 22 – Mon Jun 29"), eliding the year when both
 * endpoints share it. For surfaces where the range is the focal label (e.g.
 * the weekly coaching goal header) rather than a compact filter value.
 */
export function formatDateRangeWithWeekdays(value: DateRange): string {
  if (value.end === undefined) {
    return formatDateRange(value);
  }

  const [startDate, endDate] = computeDayBoundaries(value.end, value);
  return `${formatWeekdayEndpoint(startDate, endDate)} – ${formatWeekdayEndpoint(endDate, startDate)}`;
}

function formatAnchoredRange(end: DateTime<true>, value: DateRange): string {
  const [startDate, endDate] = computeDayBoundaries(end, value);
  return `${startDate.toLocaleString(DateTime.DATE_SHORT)} – ${endDate.toLocaleString(DateTime.DATE_SHORT)}`;
}

// The unit a relative range reads best in, left for the caller to phrase.
function measureRelativeLength(value: DateRange): {
  unit: "days" | "hours";
  count: number;
} {
  const days = value.length.as("days");
  if (days > 1.5) {
    return { unit: "days", count: Math.round(days) };
  }
  return { unit: "hours", count: Math.round(value.length.as("hours")) };
}

// Mirrors the query-side snapping (both bounds ceil to day boundaries) so
// the label always names the same days the range actually covers.
function computeDayBoundaries(
  end: DateTime<true>,
  value: DateRange,
): [DateTime, DateTime] {
  return [ceilToStartOfDay(end.minus(value.length)), endOfContainingDay(end)];
}

function formatWeekdayEndpoint(date: DateTime, otherDate: DateTime): string {
  return date.toFormat(
    date.year === otherDate.year ? "EEE MMM d" : "EEE MMM d, yyyy",
  );
}
